import type {QueryKey, QueryClient} from '@github-ui/react-query'
import {
  AuthSessionExpiredError,
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

const UNAUTHORIZED_STATUS_CODE = 401

// This is a wrapper around reactFetch that reports trace data and correctly handles SSO expiration states.
export async function fetchWithTracing<TQueryFnData = unknown, TQueryKey extends QueryKey = QueryKey>(
  apiURL: string,
  queryKey: TQueryKey,
  queryClient: QueryClient,
  mapResponse?: (data: TQueryFnData, observedAt: number) => TQueryFnData,
): Promise<TQueryFnData> {
  const response = await fetchWithErrorHandling(apiURL)

  if (response.status === UNAUTHORIZED_STATUS_CODE) {
    const previousData = queryClient.getQueryData<TQueryFnData>(queryKey)
    if (!previousData) throw new AuthSessionExpiredError()
    // When a user's SSO session expired, try to return the previous data rather than throwing an error to maintain parity with the Rails experience.
    return mapResponse
      ? mapResponse(previousData, queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? 0)
      : previousData
  }

  const json = await parseJSONWithBetterErrors(response)
  throwErrorsIfBadResponse(response)
  return mapResponse ? mapResponse(json, Date.now()) : json
}
